import { getLanguage, spanishShortDate } from "./i18n.js";
/**
 * Convert to Print — article order and paper date (2026-10-01).
 *
 * Order: the editor arranges the selected articles under "Show
 * selected" (drag the handle, or the up/down buttons) and the .docx and
 * the images .zip follow that order exactly. Before this the export
 * used click order while "Show selected" listed Library order, so what
 * the editor saw was not what they got.
 *
 * Paper date: the date the paper reaches readers. The backend rewrites
 * time references for it (backend/print_dates.py).
 *
 * Pure and framework-free — node --test frontend/scripts/printOrder.test.mjs
 */

/**
 * The print order for the current selection: ids already in `order`
 * keep their place, newly selected ids are appended in selection
 * order, and ids no longer selected are dropped. Returns a new array.
 */
export function syncOrder(order, selected) {
  const sel = selected instanceof Set ? selected : new Set(selected || []);
  const kept = (order || []).filter((id) => sel.has(id));
  const seen = new Set(kept);
  for (const id of sel) {
    if (!seen.has(id)) {
      kept.push(id);
      seen.add(id);
    }
  }
  return kept;
}

/** Move the item at `from` to final index `to`. Returns a new array. */
export function moveItem(list, from, to) {
  const arr = [...(list || [])];
  if (from < 0 || from >= arr.length) return arr;
  const target = Math.max(0, Math.min(arr.length - 1, to));
  if (target === from) return arr;
  const [item] = arr.splice(from, 1);
  arr.splice(target, 0, item);
  return arr;
}

/**
 * Final index for a dragged row: the number of OTHER rows whose
 * vertical midpoint is above the pointer. `rects` are the rows' boxes
 * ({top, height}) in the order currently shown; `draggedIdx` is where
 * the dragged row currently sits in that list.
 */
export function dropIndex(clientY, rects, draggedIdx) {
  let n = 0;
  (rects || []).forEach((r, i) => {
    if (i !== draggedIdx && r && r.top + r.height / 2 < clientY) n += 1;
  });
  return n;
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real calendar date in YYYY-MM-DD form. */
export function isValidPaperDate(value) {
  const m = ISO.exec(String(value || "").trim());
  if (!m) return false;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return (
    d.getFullYear() === Number(m[1]) &&
    d.getMonth() === Number(m[2]) - 1 &&
    d.getDate() === Number(m[3])
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "Mon, Oct 5, 2026" for a YYYY-MM-DD string, built from its parts so
 * no timezone can shift it a day (new Date("2026-10-05") is UTC
 * midnight, i.e. Oct 4 in Baja). "" when invalid.
 */
export function formatPaperDate(value) {
  if (!isValidPaperDate(value)) return "";
  const [y, mo, d] = String(value).trim().split("-").map(Number);
  const dt = new Date(y, mo - 1, d);
  if (getLanguage() === "es") return spanishShortDate(y, mo - 1, d, dt.getDay());
  return `${WEEKDAYS[dt.getDay()]}, ${MONTHS[mo - 1]} ${d}, ${y}`;
}
